import { useEffect, useRef } from 'react';

/**
 * Reclamo de la portada.
 *
 * Era un <img> apuntando a logo.gif: 1920x1080, 18 segundos, 367 fotogramas y
 * 12 MB. Al ser el elemento más grande de la mitad superior es también el que
 * mide el LCP, así que la portada no terminaba de pintar hasta descargarlo
 * entero. El mismo material en VP9/H.264 ocupa ~430 KB —un 96% menos— sin
 * pérdida visible, y el póster deja la primera imagen en pantalla de inmediato.
 *
 * Los ficheros viven en /public y no se importan: un vídeo de 400 KB no tiene
 * por qué pasar por el grafo de webpack ni invalidar el hash del bundle.
 */
export default function Hero() {
  const video = useRef(null);

  useEffect(() => {
    const element = video.current;
    if (!element) return;

    // `muted` es de los pocos atributos que React aplica como propiedad del
    // nodo, no como atributo del HTML. Los navegadores solo permiten el
    // autoplay de un vídeo silenciado, así que si la propiedad llega después de
    // que se evalúe la política de reproducción el resultado es un rectángulo
    // negro en lo primero que se ve del sitio. Se fija a mano y se pide la
    // reproducción explícitamente.
    element.muted = true;
    element.defaultMuted = true;

    // Quien haya pedido menos animación en su sistema se queda con el póster.
    const quieto = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (quieto) {
      element.pause();
      return;
    }

    // Safari devuelve una promesa rechazada si aun así bloquea el autoplay;
    // sin capturarla queda un error no gestionado en consola. El póster ya
    // cubre ese caso.
    const intento = element.play();
    if (intento && typeof intento.catch === 'function') intento.catch(() => {});
  }, []);

  return (
    <section id="inicio">
      <div className="hero-container">
        {/* Único h1 del documento. No se ve —el reclamo es la animación— pero
            es lo que nombra la marca y la actividad para los buscadores; el
            texto que lleva incrustado el vídeo no lo lee nadie. */}
        <h1 className="visually-hidden">
          Afrodita Models — Estudio de modelaje webcam en Armenia, Quindío
        </h1>

        <video
          ref={video}
          className="hero-media"
          poster="/img/hero-poster.jpg"
          width={1920}
          height={1080}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-label="Afrodita Models: transforma tu pasión en profesión"
        >
          <source src="/img/hero.webm" type="video/webm" />
          <source src="/img/hero.mp4" type="video/mp4" />
          {/* Si el navegador no reproduce ninguno de los dos, queda el póster
              como imagen fija en vez de un hueco negro. */}
          <img
            className="hero-media"
            src="/img/hero-poster.jpg"
            alt="Afrodita Models, estudio de modelaje webcam en Armenia, Quindío"
            width={1920}
            height={1080}
          />
        </video>
      </div>
    </section>
  );
}
